/**
 * PlatformProvidersPage
 * 
 * Page for managing platform-level LLM providers (System Admin).
 * Path: /settings/platform-providers
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Loader2, Server } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/i18n';
import {
  usePlatformProviders,
} from '../hooks/usePlatformProviders';
import { PlatformProviderCard } from '../components/PlatformProviderCard';
import { PlatformProviderFormModal } from '../components/PlatformProviderFormModal';
import { ModelsListModal } from '../components/ModelsListModal';
import { ProviderPolicyPanel } from '../components/ProviderPolicyPanel';
import type { PlatformProvider } from '../types';

export function PlatformProvidersPage() {
  const { t } = useI18n();
  const [showForm, setShowForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState<PlatformProvider | null>(null);
  const [managingModelsFor, setManagingModelsFor] = useState<PlatformProvider | null>(null);
  const [managingPolicyFor, setManagingPolicyFor] = useState<PlatformProvider | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Queries and mutations
  const { data: providers, isLoading } = usePlatformProviders(includeInactive);

  const handleEdit = (provider: PlatformProvider) => {
    setEditingProvider(provider);
  };

  const handleManageModels = (provider: PlatformProvider) => {
    setManagingModelsFor(provider);
  };

  const handleManagePolicy = (provider: PlatformProvider) => {
    setManagingPolicyFor(provider);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingProvider(null);
  };

  const handleFormSuccess = () => {
    setSuccessMessage(editingProvider ? 'Provider updated successfully' : 'Provider created successfully');
    setTimeout(() => setSuccessMessage(null), 3000);
    handleCloseForm();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/settings"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('admin.llmConfig.platformProviders.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('admin.llmConfig.platformProviders.description')}
            </p>
          </div>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('admin.llmConfig.platformProviders.add')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="rounded border-gray-300"
          />
          {t('admin.llmConfig.platformProviders.showInactive')}
        </label>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg text-green-700 dark:text-green-300">
          ✓ {successMessage}
        </div>
      )}

      {/* Provider cards */}
      {providers && providers.length > 0 ? (
        <div className="space-y-4">
          {providers.map((provider) => (
            <PlatformProviderCard
              key={provider.id}
              provider={provider}
              onEdit={() => handleEdit(provider)}
              onManageModels={() => handleManageModels(provider)}
              onManagePolicy={() => handleManagePolicy(provider)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 px-4 border rounded-lg bg-muted/20">
          <Server className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">{t('admin.llmConfig.platformProviders.noProviders')}</h3>
          <p className="text-muted-foreground mb-4">
            {t('admin.llmConfig.platformProviders.description')}
          </p>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('admin.llmConfig.platformProviders.addFirst')}
          </Button>
        </div>
      )}

      {/* Info section */}
      <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <h3 className="font-medium text-blue-900 dark:text-blue-100 flex items-center gap-2">
          <Server className="w-5 h-5" />
          Platform LLM Providers
        </h3>
        <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
          Platform providers are configured at the system level and are available to all tenants.
          Credentials are stored securely in Klviar and are never exposed in API responses.
        </p>
        <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 list-disc list-inside space-y-1">
          <li>Configure Azure AI Foundry (LLM), AWS Bedrock (LLM) and Voyage AI (Embeddings) providers</li>
          <li>Health checks monitor provider and model availability</li>
          <li>Use the Policy panel to control model discovery and activation rules</li>
        </ul>
      </div>

      {/* Create/Edit form modal */}
      {(showForm || editingProvider) && (
        <PlatformProviderFormModal
          provider={editingProvider}
          onClose={handleCloseForm}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Models list modal */}
      {managingModelsFor && (
        <ModelsListModal
          provider={managingModelsFor}
          onClose={() => setManagingModelsFor(null)}
        />
      )}

      {/* Policy panel modal */}
      {managingPolicyFor && (
        <ProviderPolicyPanel
          provider={managingPolicyFor}
          onClose={() => setManagingPolicyFor(null)}
        />
      )}
    </div>
  );
}

export default PlatformProvidersPage;
